import type { RelayServerIdentity } from "@palestra/core";

/**
 * Chi sta girando su questo dispositivo, e con chi sta parlando.
 *
 * Questo modulo produce le due righe che chiudono la schermata Profilo. Il
 * bundle React si dichiara quando viene costruito; il relay Go si dichiara nel
 * messaggio `ready`. Sono fatti osservati, non supposizioni sul deploy.
 */

export interface BuildStamp {
  variant: "react";
  /** La versione del workspace: `0.1.0`, oppure `dev` se il manifest non si è letto. */
  version: string;
  /** Istante ISO in cui il bundle è stato prodotto. */
  builtAt: string;
  /** Commit abbreviato, vuoto quando la build non sapeva quale fosse. */
  commit: string;
}

/**
 * Sostituito testualmente da Vite (`define`). Il `typeof` che segue non è
 * pignoleria: sotto Vitest la sostituzione non avviene — la suite gira con la
 * configurazione della radice, non con quella dell'app — e senza guardia il
 * modulo esploderebbe al primo import.
 */
declare const __PALESTRA_BUILD__: BuildStamp;

const UNSTAMPED: BuildStamp = {
  variant: "react",
  version: "dev",
  builtAt: "",
  commit: "",
};

export const buildStamp: BuildStamp =
  typeof __PALESTRA_BUILD__ === "undefined" ? UNSTAMPED : __PALESTRA_BUILD__;

/** Un istante leggibile da chi guarda il telefono, non da chi legge i log. */
export function formatBuildInstant(instant: string, locale = "it-IT"): string | undefined {
  if (!instant) return undefined;
  const built = new Date(instant);
  if (Number.isNaN(built.getTime())) return undefined;
  return built.toLocaleString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * «React · v0.1.0 · build del 4 agosto 2026, 16:42 · e0304f1»
 *
 * I pezzi che mancano spariscono invece di comparire vuoti: una build senza
 * commit — quelle prodotte fuori dal repository — non deve mostrare un trattino
 * solitario che sembra un guasto.
 */
export function formatBuildLine(stamp: BuildStamp = buildStamp, locale = "it-IT"): string {
  const built = formatBuildInstant(stamp.builtAt, locale);
  return [
    "React",
    `v${stamp.version}`,
    built ? `build del ${built}` : "build senza data",
    stamp.commit,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Il relay che ha risposto. `undefined` non è «nessuno»: è «non ci ho ancora
 * parlato», e va detto così — al primo avvio, offline, la riga esiste comunque.
 * Un relay che non si dichiara è un relay più vecchio di questo campo, ed è
 * un'informazione anch'essa.
 */
export function formatRelayLine(relay: RelayServerIdentity | undefined): string {
  if (!relay) return "Relay: nessun contatto ancora";
  const kind = relay.kind === "go" ? "Go (palestrad)" : "precedente (Node)";
  return `Relay: ${kind} · ${relay.version}`;
}
